import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useConfirm } from "../../hooks/useConfirm";
import { useAuth } from "../../context/AuthContext";
import { GitBranch, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@shadcn-in-nx/ui/button";
import { Alert, AlertDescription } from "@shadcn-in-nx/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import FunctionShareDialog from "./FunctionShareDialog";

function FunctionSharingPanel({ workflowId }) {
    const { t } = useTranslation();
    const { isFeatureEnabled } = useAuth();
    const [confirm, ConfirmDialog] = useConfirm();
    const [outgoingGrants, setOutgoingGrants] = useState([]);
    const [incomingGrants, setIncomingGrants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showShareDialog, setShowShareDialog] = useState(false);

    const { call: fetchGrants } = useBluelagoon({ method: "getFunctionGrants" });
    const { call: deleteGrant, isLoading: isDeleting } = useBluelagoon({ method: "deleteFunctionGrant" });

    const loadData = useCallback(async () => {
        if (!workflowId) return;
        setIsLoading(true);
        try {
            const res = await fetchGrants({ workflowId });
            if (res?.data) {
                setOutgoingGrants(res.data.outgoingGrants || []);
                setIncomingGrants(res.data.incomingGrants || []);
            }
        } catch (e) {
            toast.error(t("appPreview.loadGrantsFailed"));
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, fetchGrants]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleRevoke = async (grantId) => {
        try {
            await confirm({
                title: t("appPreview.revokeSharedFunction"),
                description: t("appPreview.revokeDescription"),
                isDestructive: true,
            });
        } catch {
            return;
        }
        try {
            await deleteGrant({ grantId });
            toast.success(t("appPreview.grantRevoked"));
            loadData();
        } catch {
            toast.error(t("appPreview.grantRevokeFailed"));
        }
    };

    if (isLoading) {
        return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
    }

    return (
        <div className="p-6 space-y-6 w-full">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <GitBranch className="w-5 h-5" />
                    <h2 className="text-lg font-semibold">{t("appPreview.functionSharing")}</h2>
                </div>
                <Button size="sm" onClick={() => setShowShareDialog(true)}><Plus className="w-4 h-4 mr-1" />{t("appPreview.shareFunction")}</Button>
            </div>
            {outgoingGrants.length === 0 && incomingGrants.length === 0 && (
                <Alert><AlertDescription>{t("appPreview.noSharedFunctions")}</AlertDescription></Alert>
            )}
            {outgoingGrants.length > 0 && (
                <Card>
                    <CardHeader><CardTitle className="text-sm">{t("appPreview.sharedByThisApp")}</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {outgoingGrants.map((g) => (
                            <div key={g.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div>
                                    <span className="font-medium text-sm">{g.component?.name}</span>
                                    <Badge variant="outline" className="ml-2 text-xs">{g.grantedWorkflow?.name}</Badge>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => handleRevoke(g.id)} disabled={isDeleting}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
            {incomingGrants.length > 0 && (
                <Card>
                    <CardHeader><CardTitle className="text-sm">{t("appPreview.sharedWithThisApp")}</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {incomingGrants.map((g) => (
                            <div key={g.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="font-medium text-sm">{g.component?.name}</span>
                                <Badge variant="secondary" className="text-xs">from {g.sourceWorkflow?.name}</Badge>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
            {showShareDialog && <FunctionShareDialog workflowId={workflowId} onClose={() => setShowShareDialog(false)} onCreated={loadData} />}
            <ConfirmDialog />
        </div>
    );
}

export default FunctionSharingPanel;
